import { useCallback, useState } from 'react';
import { Button } from '@framework/ui/atoms/Button';
import { useCurrentUserEntity } from '@user/UserEntity/useCurrentUserEntity';
import { useNavigate } from 'react-router';
import { UserPagePathBuilder } from '../UserPagePathBuilder';
import { Loading } from '@framework/ui/atoms';
import { createOnboardingViewModel } from '../GroupMemberInvitationPage/AuthenticatedMenu/createOnboardingViewModel';
import { UserKey } from '@user/domain';
import { useActionLogSender } from '@framework/action-log';
import { useCurrentSessionId } from '@framework/auth/authStatus';
import { GroupIpRestrictionCheckRequest } from '@group/AccessControl';
import { WorkspaceOperation } from '@workspace/domain/WorkspaceOperation';
import { trpcClient } from '@framework/trpcClient';
import { GROUP_DEFAULT_MODELING_FORMAT_I18N_KEYS } from '@framework/i18n/modelingFormatKeys';
import { useTranslation } from 'react-i18next';
import { ModelingFormatList, type ModelingFormat } from 'balus-common';

type Props = {
  groupCount: number;
  handleBackToGroupSelect: () => void;
};

export const CreateGroup: React.FC<Props> = ({ groupCount, handleBackToGroupSelect }: Props) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // 現在のユーザの言語設定をもとに、作成するグループのデフォルトモデリングフォーマットを設定する
  const [defaultModelingFormat, setDefaultModelingFormat] = useState<ModelingFormat>(
    (i18n.resolvedLanguage ?? i18n.language) === 'en' ? 'LATIN' : 'CJK'
  );

  const navigate = useNavigate();
  const userEntity = useCurrentUserEntity();
  const actionLogSender = useActionLogSender();

  const currentSessionId = useCurrentSessionId();

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setGroupName(event.target.value);
  }, []);

  const disabledSubmit = groupName.trim() === '';

  const handleSubmit = useCallback(async () => {
    if (!userEntity) {
      setErrorMessage(t('home.createGroup.errorUserInfo'));
      return;
    }

    setLoading(true);

    try {
      const { groupId } = await trpcClient.group.createGroupAndAssignSelfAsAdmin.mutate({
        groupName,
        defaultModelingFormat,
      });

      // IPアドレスアクセス制御用データが準備できるまで待機
      await GroupIpRestrictionCheckRequest.waitAllow(groupId, currentSessionId);

      const workspaceId = await WorkspaceOperation.createPersonal(
        groupId,
        userEntity.id,
        t('workspace.personalWorkspace.name')
      );

      // オンボーディング用ビューモデルを作成する
      const onboardingViewModelId = await createOnboardingViewModel(
        groupId,
        UserKey.buildFromId(userEntity.id),
        workspaceId,
        actionLogSender
      );

      if (onboardingViewModelId) {
        // ビューモデル作成に成功した場合
        navigate(UserPagePathBuilder.viewModelPage(onboardingViewModelId));
      } else {
        navigate(UserPagePathBuilder.groupPage(groupId));
      }
    } catch (error) {
      setErrorMessage(t('home.createGroup.errorCreate'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [userEntity, t, groupName, defaultModelingFormat, currentSessionId, actionLogSender, navigate]);

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="flex flex-col p-4" style={{ width: '600px' }}>
            <div className="pb-2">
              <div className="flex flex-col py-2">
                <label htmlFor="group-name" className="font-bold">
                  {t('home.createGroup.nameLabel')}
                </label>
                <input
                  type="text"
                  id="group-name"
                  value={groupName}
                  onChange={handleChange}
                  className="rounded-sm border border-gray-500 px-2 py-1"
                  autoFocus
                />
              </div>
              <div className="py-2">{t('home.createGroup.trialDescription')}</div>
            </div>

            <div className="pb-2">
              <div className="flex flex-col py-2">
                <label htmlFor="default-modeling-format">{t('home.createGroup.defaultModelingFormatLabel')}</label>
                <select
                  id="default-modeling-format"
                  className="form-select m-1 py-1 text-sm"
                  value={defaultModelingFormat}
                  onChange={(e) => setDefaultModelingFormat(e.target.value as ModelingFormat)}
                >
                  {ModelingFormatList.map((mode) => (
                    <option key={mode} value={mode}>
                      {t(GROUP_DEFAULT_MODELING_FORMAT_I18N_KEYS[mode])}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {errorMessage && <div className="py-2 text-red-700">{errorMessage}</div>}
          </div>
          <div className={'flex justify-end py-2'}>
            <div className={'pr-2'}>
              <Button color="primary" onClick={handleSubmit} loading={loading} disabled={disabledSubmit}>
                {t('home.createGroup.submitButton')}
              </Button>
            </div>
            {groupCount > 0 && (
              <div className={'pr-2'}>
                <Button color="secondary" onClick={handleBackToGroupSelect}>
                  {t('common.label.backToGroupSelection')}
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};
